import { zodResolver } from "@hookform/resolvers/zod";
import { Copy, Info, Plus } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import {
  FormProvider,
  Controller,
  useFieldArray,
  useForm,
  useWatch,
  type FieldErrors,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { apiClient } from "@/api/client";
import { apiErrorMessage } from "@/api/errors";
import type { components } from "@/api/schema";
import { toast } from "@/shared/lib/toast";

import { useActivateLinkedGroup } from "../api/use-activate-linked-group";
import { useClonePromotion } from "../api/use-clone-promotion";
import { useCloneToMarketplaces } from "../api/use-clone-to-marketplaces";
import { useDeletePromotion } from "../api/use-delete-promotion";
import { useLinkedGroup } from "../api/use-linked-group";
import { usePromotion } from "../api/use-promotion";
import { useUpsertPromotion } from "../api/use-upsert-promotion";
import { reasonFieldPath, type ActivationReason } from "../lib/activation-reasons";
import { toDatetimeLocal, toIso } from "../lib/datetime-local";
import {
  promotionDraftSchema,
  type PromotionDraftFormInput,
  type PromotionDraftFormValues,
} from "../lib/promotion-schema";

import { RadioPillGroup, SectionCard } from "./builder-controls";
import { PrimaryButton, SecondaryButton } from "./buttons";
import { RuleCard } from "./RuleCard";
import { SellerSelect } from "./SellerSelect";

type Promotion = components["schemas"]["Promotion"];
type RuleUpsert = components["schemas"]["RuleUpsert"];
type Marketplace = Promotion["marketplace"];

interface PromotionBuilderProps {
  promotionId: string;
  /** Precheck reasons from ActivatePanel (via the page); mapped onto form fields. */
  precheckReasons?: ActivationReason[] | null;
  /**
   * Hands the page a stable imperative save fn so Activate/Simulate can persist
   * the current builder edits BEFORE acting on the server (the edits otherwise
   * live only in RHF state until Save). Resolves `true` on a committed upsert,
   * `false` when validation blocks the save or the upsert errors.
   */
  onRegisterSave?: (save: () => Promise<boolean>) => void;
  /**
   * Reports the linked-group id + a busy flag whenever this promotion belongs to
   * a linked draft group at all (regardless of remaining member count) — the page
   * threads these into ActivatePanel, which derives the member count itself
   * (`useLinkedGroup`) and decides WHETHER to switch to "Activate for N channels".
   * Registering unconditionally on `groupId` (rather than gating on member count
   * here too) keeps `disabled` from freezing stale once a group degrades to a
   * single remaining member. Mirrors `onRegisterSave`: a stable handle the
   * builder pushes up rather than the page reaching down.
   */
  onRegisterActivateGroup?: (groupId: string, disabled: boolean) => void;
  /**
   * promotions:manage missing → render the same layout with no Save/Clone bar
   * and every control disabled. The viewer can still read rules and use
   * Simulate (promotions:read).
   */
  readOnly?: boolean;
}

/**
 * Builds the PUT body from validated form values. Extracted so the Save button's
 * `onSubmit` and the imperative `saveDraft` (Activate/Simulate save-first) emit
 * an identical request.
 */
function buildUpsertBody(
  values: PromotionDraftFormValues,
): components["schemas"]["PromotionUpsertRequest"] {
  return {
    name: values.name,
    ...(values.note ? { note: values.note } : {}),
    ...(values.note_en ? { note_en: values.note_en } : {}),
    ...(values.note_th ? { note_th: values.note_th } : {}),
    marketplace: values.marketplace,
    seller_id: values.seller_id,
    start_at: toIso(values.start_at),
    end_at: toIso(values.end_at),
    rule_mode: values.rule_mode,
    rules: values.rules.map((r, i): RuleUpsert => ({
      ...(r.id ? { id: r.id } : {}),
      sequence: i + 1,
      rule_type: r.rule_type,
      payment_method: r.payment_method,
      min_spend_value: r.min_spend_value,
      is_min_spend: r.is_min_spend,
      discount: r.discount,
      shipping_fee: r.shipping_fee,
      gift_limit_quantity: r.gift_limit_quantity,
      gift_limit_type: r.gift_limit_type,
      gift_used_offset: r.gift_used_offset ?? 0,
      products: r.products.map((p) => ({
        sku: p.sku,
        kind: p.kind,
        is_freebie: p.is_freebie,
        // `quantity` is `number | null` on the wire; omit when undefined so the
        // optional-vs-undefined gap (exactOptionalPropertyTypes) stays closed.
        ...(p.quantity === undefined ? {} : { quantity: p.quantity }),
      })),
    })),
  };
}

const FIELD =
  "h-9 w-full rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-3 text-sm outline-none transition focus:border-[var(--brand-500)] focus-ring disabled:opacity-50";
const LABEL =
  "mb-1 block text-[11px] font-bold uppercase tracking-[0.05em] text-[var(--text-subtle)]";

function emptyRule(sequence: number): PromotionDraftFormInput["rules"][number] {
  return {
    sequence,
    rule_type: "all",
    payment_method: "ALL",
    min_spend_value: "0.00",
    is_min_spend: false,
    discount: "include",
    shipping_fee: "exclude",
    gift_limit_quantity: 1,
    gift_limit_type: "PER_ORDER",
    gift_used_offset: 0,
    products: [],
  };
}

function toFormValues(p: Promotion): PromotionDraftFormInput {
  return {
    name: p.name,
    note: p.note ?? undefined,
    note_en: p.note_en ?? undefined,
    note_th: p.note_th ?? undefined,
    marketplace: p.marketplace,
    seller_id: p.seller_id,
    // datetime-local inputs require `YYYY-MM-DDTHH:mm` (no zone); the API returns
    // RFC3339 with an offset, which a real browser would reject -> blank field ->
    // failed Save. Convert to the input format here, back to ISO in onSubmit.
    start_at: toDatetimeLocal(p.start_at),
    end_at: toDatetimeLocal(p.end_at),
    rule_mode: p.rule_mode,
    rules: (p.rules ?? []).map((r) => ({
      id: r.id,
      sequence: r.sequence,
      rule_type: r.rule_type,
      payment_method: r.payment_method,
      min_spend_value: r.min_spend_value,
      is_min_spend: r.is_min_spend,
      discount: r.discount,
      shipping_fee: r.shipping_fee,
      gift_limit_quantity: r.gift_limit_quantity,
      gift_limit_type: r.gift_limit_type,
      gift_used_offset: r.gift_used_offset ?? 0,
      gift_used: r.gift_used,
      gift_remaining: r.gift_remaining ?? undefined,
      products: r.products.map((pr) => ({
        sku: pr.sku,
        kind: pr.kind,
        quantity: pr.quantity ?? 1,
        is_freebie: pr.is_freebie,
      })),
    })),
  };
}

export function PromotionBuilder({
  promotionId,
  precheckReasons,
  onRegisterSave,
  onRegisterActivateGroup,
  readOnly = false,
}: PromotionBuilderProps): JSX.Element {
  const { t } = useTranslation("promotion");
  const detail = usePromotion(promotionId);
  const groupId = detail.data?.linked_group_id ?? null;
  const isDraft = detail.data?.status === "draft";
  const linkedGroup = useLinkedGroup(isDraft ? groupId : null);
  const deletePromotion = useDeletePromotion();
  const cloneToMarketplaces = useCloneToMarketplaces();
  const activateGroup = useActivateLinkedGroup();
  const groupMembers = linkedGroup.data?.items ?? [];
  const groupMarketplaces = new Set(groupMembers.map((m) => m.marketplace));
  // Marketplace whose checkbox is checked but has no group member yet — shows
  // an inline SellerSelect instead of cloning immediately, mirroring how
  // PromotionFormModal collects a seller per marketplace before submitting.
  const [pendingChannel, setPendingChannel] = useState<Marketplace | null>(null);
  const upsert = useUpsertPromotion();
  const updateSiblingSeller = useUpsertPromotion();
  const clone = useClonePromotion();

  // Save / Save-draft binds to the LENIENT draft schema: only the basics
  // (name, marketplace, seller_id, end-after-start) gate a save. Rules and rule
  // completeness are NOT required here — the BE upsert accepts incomplete work,
  // and completeness is enforced only at Activate. See promotion-schema.ts.
  const methods = useForm<PromotionDraftFormInput, unknown, PromotionDraftFormValues>({
    resolver: zodResolver(promotionDraftSchema),
    defaultValues: {
      name: "",
      marketplace: "shopee",
      seller_id: "",
      start_at: "",
      end_at: "",
      rule_mode: "all",
      rules: [],
    },
  });
  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    setFocus,
    formState: { errors },
  } = methods;
  const marketplace = useWatch({ control, name: "marketplace" });
  const rules = useFieldArray({ control, name: "rules" });

  // Hydrate the form from the loaded promotion exactly once; re-hydrating on
  // every render would clobber edits (e.g. clearing a field) the moment React
  // Query hands back a fresh reference.
  const hydrated = useRef(false);
  const detailData = detail.data;
  useEffect(() => {
    if (detailData && !hydrated.current) {
      hydrated.current = true;
      reset(toFormValues(detailData));
    }
  }, [detailData, reset]);

  // Map blocked-precheck reasons onto the fields they name so the RuleCard can
  // paint them (the "blue highlight" QA saw was only the focus ring).
  useEffect(() => {
    if (!precheckReasons?.length) return;
    let focused = false;
    for (const r of precheckReasons) {
      const path = reasonFieldPath(r);
      if (!path) continue;
      setError(path as Parameters<typeof setError>[0], {
        type: "precheck",
        message: `precheck.reason.${r.code}`,
      });
      if (!focused && path.endsWith("gift_limit_quantity")) {
        setFocus(path as Parameters<typeof setFocus>[0]);
        focused = true;
      }
    }
  }, [precheckReasons, setError, setFocus]);

  // The form-submit path and the imperative `saveDraft` share one upsert call.
  // `resolve` (when present) lets `saveDraft` report committed/errored back to the
  // caller (Activate/Simulate save-first); the plain Save button passes none.
  const submitUpsert = useCallback(
    (values: PromotionDraftFormValues, resolve?: (ok: boolean) => void): void => {
      upsert.mutate(
        { id: promotionId, body: buildUpsertBody(values) },
        {
          onSuccess: () => {
            toast.success(t("builder.saved"));
            resolve?.(true);
          },
          onError: (err) => {
            toast.error(apiErrorMessage(err));
            resolve?.(false);
          },
        },
      );
    },
    [promotionId, upsert, t],
  );

  function onSubmit(values: PromotionDraftFormValues): void {
    submitUpsert(values);
  }

  // A blocked save (invalid basics/dates) must EXPLAIN itself instead of doing
  // nothing — that silent no-op was the reported bug. Inline field errors already
  // render under each control; this surfaces a toast naming the first blocker so a
  // user who clicked Save off-screen still gets feedback. The schema messages are
  // i18n keys (e.g. `builder.err_name_required`); we translate them and prefix with
  // the generic header. Field order = the order the user reads the form.
  function onInvalid(formErrors: FieldErrors<PromotionDraftFormInput>): void {
    const firstKey = (["name", "seller_id", "start_at", "end_at"] as const).find(
      (k) => formErrors[k],
    );
    let msgKey = firstKey ? formErrors[firstKey]?.message : undefined;
    let rulePrefix: string | null = null;
    if (!msgKey) {
      // No top-level blocker: check per-rule errors (e.g. quota_zero, missing_add_on)
      // so the toast names which rule and field, not just "fix the highlighted fields".
      const ruleErrors = formErrors.rules;
      const ruleIndex = Array.isArray(ruleErrors)
        ? ruleErrors.findIndex((r) => r?.gift_limit_quantity ?? r?.gift_used_offset ?? r?.products)
        : -1;
      if (ruleIndex >= 0) {
        const r = ruleErrors?.[ruleIndex];
        msgKey =
          r?.gift_limit_quantity?.message ??
          r?.gift_used_offset?.message ??
          (r?.products as { message?: string } | undefined)?.message;
        rulePrefix = t("precheck.rule_prefix", { n: ruleIndex + 1 });
      }
    }
    const detail = msgKey ? t(msgKey) : t("builder.err_fix_required");
    const named = rulePrefix ? `${rulePrefix}: ${detail}` : detail;
    toast.error(`${t("builder.err_cannot_save")}: ${named}`);
  }

  // Imperative save used by Activate/Simulate "save-first". Runs the SAME
  // validation + submit as the Save button (handleSubmit → validSubmit | onInvalid),
  // and resolves `true` only after the upsert commits, `false` on a validation
  // block or upsert error. Stable identity (useCallback) so the page can register
  // it once.
  const saveDraft = useCallback(
    (): Promise<boolean> =>
      new Promise<boolean>((resolve) => {
        void handleSubmit(
          (values) => submitUpsert(values, resolve),
          (formErrors) => {
            onInvalid(formErrors);
            resolve(false);
          },
        )();
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleSubmit/onInvalid are stable per render; submitUpsert is memoized.
    [handleSubmit, submitUpsert],
  );

  // Register the imperative save once (and on identity change) so the page holds
  // a stable handle to persist current edits before Activate/Simulate.
  useEffect(() => {
    if (readOnly) return;
    onRegisterSave?.(saveDraft);
  }, [onRegisterSave, saveDraft, readOnly]);

  // Report the linked-group id + a busy flag up to the page whenever this
  // promotion belongs to a linked draft group — the page threads these into
  // ActivatePanel so its button can switch to group-activate mode. Fires
  // unconditionally on `groupId` (NOT gated on `groupMembers.length > 1`): the
  // "N channels" display gating is ActivatePanel's own concern (it re-derives
  // member count itself via `useLinkedGroup`). Gating the registration here too
  // would let `disabled` go stale — once membership degrades to 1 remaining
  // member (the last sibling just removed), a length-gated effect would stop
  // firing altogether and the page's `disabled` would freeze at whatever value
  // was last pushed (possibly `true`, captured mid-mutation). Standalone
  // promotions (`groupId` null) never call this, so the page's Activate control
  // stays in single-promotion mode.
  const groupBusy =
    cloneToMarketplaces.isPending || deletePromotion.isPending || activateGroup.isPending;
  useEffect(() => {
    if (groupId) {
      onRegisterActivateGroup?.(groupId, groupBusy);
    }
  }, [onRegisterActivateGroup, groupId, groupMembers.length, groupBusy]);

  function onClone(): void {
    clone.mutate(promotionId, {
      onSuccess: () => toast.success(t("detail.cloned")),
      onError: (err) => toast.error(apiErrorMessage(err)),
    });
  }

  // Re-target an already-linked sibling's seller while the group is still draft
  // (ADR-0021: seller is per-marketplace, never inherited). The linked-group
  // listing omits rules, so the sibling's full row is fetched fresh and re-sent
  // through the same upsert body builder the primary form uses, with only
  // seller_id swapped — rules are identical across draft siblings by the
  // propagation the backend already does on every rule save.
  async function onEditSiblingSeller(memberId: string, sellerId: string): Promise<void> {
    const { data, error } = await apiClient.GET("/v1/promotions/{id}", {
      params: { path: { id: memberId } },
    });
    if (error || !data) {
      toast.error(apiErrorMessage(error));
      return;
    }
    const values = promotionDraftSchema.parse(toFormValues(data));
    updateSiblingSeller.mutate(
      { id: memberId, body: buildUpsertBody({ ...values, seller_id: sellerId }) },
      { onError: (err) => toast.error(apiErrorMessage(err)) },
    );
  }

  const persisted = Boolean(detailData);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate data-testid="promotion-builder">
        {/* Builder action bar (mockup .builder-head right cluster). */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-[var(--control-r)] border border-[var(--premium-border)] bg-[var(--canvas)] px-2.5 py-1.5 text-[12px] text-[var(--text-subtle)]">
            <Info className="h-3.5 w-3.5" aria-hidden />
            {t("detail.activate_note")}
          </span>
          {!readOnly && (
            <div className="ml-auto flex items-center gap-2">
              <SecondaryButton
                type="submit"
                data-testid="builder-save-draft"
                disabled={upsert.isPending}
              >
                {t("builder.save_draft")}
              </SecondaryButton>
              <SecondaryButton
                type="button"
                data-testid="builder-clone"
                disabled={!persisted || clone.isPending}
                onClick={onClone}
              >
                <Copy className="h-4 w-4" aria-hidden />
                {t("action.clone")}
              </SecondaryButton>
              <PrimaryButton type="submit" data-testid="builder-save" disabled={upsert.isPending}>
                {t("builder.save")}
              </PrimaryButton>
            </div>
          )}
        </div>

        {/* Read-only (spec §2): the form body sits in a disabled fieldset; the
            rules list sits outside it so each RuleCard keeps its collapse
            toggle clickable and disables only its own body. Edit actions
            (add/remove/move/pick) are hidden, not disabled. */}
        <div className="space-y-4" {...(readOnly ? { "data-testid": "builder-readonly" } : {})}>
          <fieldset disabled={readOnly} className="min-w-0 border-0 p-0 m-0">
            <div className="space-y-4">
              {/* ── Section 1: Basics ── */}
              <SectionCard title={t("detail.section.basics")} data-testid="builder-section-basics">
                <div>
                  <label className={LABEL} htmlFor="builder-name-input">
                    {t("detail.field.name")}
                  </label>
                  <input
                    id="builder-name-input"
                    data-testid="builder-name"
                    className={FIELD}
                    {...register("name")}
                  />
                  {errors.name && (
                    <p
                      className="mt-1 text-xs text-[var(--intent-danger-fg)]"
                      data-testid="builder-name-error"
                    >
                      {t(errors.name.message ?? "")}
                    </p>
                  )}
                </div>
                <div className="mt-3">
                  <label className={LABEL} htmlFor="builder-note-input">
                    {t("detail.field.note")}
                  </label>
                  <input
                    id="builder-note-input"
                    data-testid="builder-note"
                    className={FIELD}
                    {...register("note")}
                  />
                </div>
                {/* Bilingual descriptions (mockup Basics: EN / ไทย). */}
                <div className="mt-3 grid gap-4 md:grid-cols-2">
                  <div>
                    <label className={LABEL} htmlFor="builder-note-en-input">
                      {t("detail.field.note_en")}
                    </label>
                    <textarea
                      id="builder-note-en-input"
                      data-testid="builder-note-en"
                      rows={2}
                      placeholder={t("detail.field.note_en_placeholder")}
                      className={`${FIELD} h-auto py-2 leading-relaxed`}
                      {...register("note_en")}
                    />
                  </div>
                  <div>
                    <label className={LABEL} htmlFor="builder-note-th-input">
                      {t("detail.field.note_th")}
                    </label>
                    <textarea
                      id="builder-note-th-input"
                      data-testid="builder-note-th"
                      rows={2}
                      placeholder={t("detail.field.note_th_placeholder")}
                      className={`${FIELD} h-auto py-2 leading-relaxed`}
                      {...register("note_th")}
                    />
                  </div>
                </div>
              </SectionCard>

              {/* ── Section 2: Targeting ── */}
              <SectionCard
                title={t("detail.section.targeting")}
                data-testid="builder-section-targeting"
              >
                <div className="space-y-3">
                  {groupId && groupMembers.length > 1 && (
                    <div
                      className="flex items-center gap-1.5 rounded-[var(--control-r)] border border-[var(--premium-border)] bg-[var(--canvas)] px-2.5 py-1.5 text-[12px] text-[var(--text-subtle)]"
                      data-testid="builder-linked-group-banner"
                    >
                      {t("builder.linked_group_banner", { count: groupMembers.length })}
                    </div>
                  )}
                  <div>
                    <label className={LABEL}>{t("detail.field.marketplace")}</label>
                    <div className="space-y-2">
                      {(["shopee", "lazada", "tiktok"] as const).map((mkp) => {
                        const member = groupMembers.find((m) => m.marketplace === mkp);
                        const isPrimary = mkp === marketplace;
                        const isPending = pendingChannel === mkp;
                        const isChecked = isPrimary || groupMarketplaces.has(mkp) || isPending;
                        return (
                          <div key={mkp} className="flex items-center gap-3">
                            <label className="flex w-28 items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                data-testid={`builder-channel-${mkp}`}
                                checked={isChecked}
                                disabled={isPrimary || !isDraft}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setPendingChannel(mkp);
                                  } else if (member) {
                                    deletePromotion.mutate(member.id, {
                                      onError: (err) => toast.error(apiErrorMessage(err)),
                                    });
                                  } else {
                                    setPendingChannel(null);
                                  }
                                }}
                              />
                              {mkp === "shopee" ? "Shopee" : mkp === "lazada" ? "Lazada" : "TikTok"}
                            </label>
                            {isPrimary ? (
                              <Controller
                                control={control}
                                name="seller_id"
                                render={({ field }) => (
                                  <SellerSelect
                                    id="builder-seller-input"
                                    testId="builder-seller"
                                    marketplace={marketplace}
                                    value={field.value}
                                    onChange={field.onChange}
                                    className={FIELD}
                                  />
                                )}
                              />
                            ) : isPending ? (
                              <SellerSelect
                                id={`builder-seller-${mkp}-input`}
                                testId={`builder-seller-${mkp}`}
                                marketplace={mkp}
                                value=""
                                onChange={(sellerId) => {
                                  if (!sellerId) return;
                                  cloneToMarketplaces.mutate(
                                    {
                                      id: promotionId,
                                      targets: [{ marketplace: mkp, seller_id: sellerId }],
                                    },
                                    {
                                      onSuccess: () => setPendingChannel(null),
                                      onError: (err) => toast.error(apiErrorMessage(err)),
                                    },
                                  );
                                }}
                                className={FIELD}
                              />
                            ) : (
                              member && (
                                <SellerSelect
                                  id={`builder-seller-${mkp}-input`}
                                  testId={`builder-seller-${mkp}`}
                                  marketplace={mkp}
                                  value={member.seller_id}
                                  onChange={(sellerId) => {
                                    if (!sellerId || sellerId === member.seller_id) return;
                                    void onEditSiblingSeller(member.id, sellerId);
                                  }}
                                  className={FIELD}
                                />
                              )
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label className={LABEL}>{t("detail.field.active_period")}</label>
                    <div className="grid gap-3 md:grid-cols-2">
                      <input
                        type="datetime-local"
                        data-testid="builder-start"
                        className={FIELD}
                        {...register("start_at")}
                      />
                      <input
                        type="datetime-local"
                        data-testid="builder-end"
                        className={FIELD}
                        {...register("end_at")}
                      />
                    </div>
                    {errors.end_at && (
                      <p
                        className="mt-1 text-xs text-[var(--intent-danger-fg)]"
                        data-testid="builder-dates-error"
                      >
                        {t(errors.end_at.message ?? "")}
                      </p>
                    )}
                  </div>
                </div>
              </SectionCard>

              {/* ── Section 3: Rule mode ── */}
              <SectionCard
                title={t("detail.section.rule_mode")}
                data-testid="builder-section-rule-mode"
              >
                <Controller
                  control={control}
                  name="rule_mode"
                  render={({ field }) => (
                    <RadioPillGroup
                      name={t("detail.field.rule_mode")}
                      data-testid="builder-rule-mode"
                      value={field.value}
                      onChange={field.onChange}
                      options={[
                        { value: "all", label: t("detail.rule_mode.stack_all") },
                        { value: "break", label: t("detail.rule_mode.first_match") },
                      ]}
                    />
                  )}
                />
                <p className="mt-3 flex items-start gap-2 rounded-lg border border-[var(--premium-border)] bg-[var(--canvas)] px-3 py-2 text-[12px] leading-relaxed text-[var(--text-subtle)]">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  {t("detail.rule_mode_note")}
                </p>
              </SectionCard>
            </div>
          </fieldset>

          {/* ── Rules ── */}
          <div className="space-y-3.5">
            {rules.fields.map((field, i) => (
              <RuleCard
                key={field.id}
                index={i}
                control={control}
                readOnly={readOnly}
                onRemove={() => rules.remove(i)}
                onMoveUp={i > 0 ? () => rules.move(i, i - 1) : undefined}
                onMoveDown={i < rules.fields.length - 1 ? () => rules.move(i, i + 1) : undefined}
              />
            ))}
            {errors.rules?.message && (
              <p
                className="text-xs text-[var(--intent-danger-fg)]"
                data-testid="builder-rules-error"
              >
                {t(errors.rules.message)}
              </p>
            )}
            {!readOnly && (
              <div className="flex justify-center pt-1">
                <button
                  type="button"
                  data-testid="builder-add-rule"
                  onClick={() => rules.append(emptyRule(rules.fields.length))}
                  className="inline-flex h-9 items-center gap-2 rounded-[var(--control-r)] border border-dashed border-[var(--premium-border-strong)] px-4 text-[13px] font-semibold text-[var(--text-secondary)] transition hover:border-[var(--brand-500)] hover:bg-[var(--row-selected)] hover:text-[var(--brand-700)]"
                >
                  <Plus className="h-4 w-4" aria-hidden /> {t("detail.add_rule")}
                </button>
              </div>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
